import type { ActionType } from '@/api/actionTypesApi'

export const BUILT_IN_ACTION_TYPES = [
	'update_firmware',
	'update_config',
	'update_geofence',
	'send_file',
	'launch_shell',
	'send_script',
	'cancel_action',
	'retry_action',
	'reprovision',
	'detach',
	'attach',
] as const

export type BuiltInActionType = (typeof BUILT_IN_ACTION_TYPES)[number]

export const ORGANIZATION_ACTION_TYPES = ['reprovision', 'detach', 'attach'] as const

export type OrganizationActionType = (typeof ORGANIZATION_ACTION_TYPES)[number]

export function isOrganizationActionType(type: string): type is OrganizationActionType {
	return (ORGANIZATION_ACTION_TYPES as readonly string[]).includes(type)
}

// Org rows all have type 'renew_cert'; the backend splits them via its `reprovision_type` param.
const ORG_ACTION_LABEL_TO_REPROVISION_TYPE = new Map<string, string>([
	['attach', 'attach'],
	['detach', 'detach'],
	['reprovision', 'transfer'],
])

/**
 * Backend `reprovision_type` for an org action label, or undefined. A Map, not an object: labels
 * come from URL segments and chips, where a key like `constructor` would resolve to something truthy.
 */
export function toReprovisionType(label: string | undefined): string | undefined {
	return label ? ORG_ACTION_LABEL_TO_REPROVISION_TYPE.get(label.toLowerCase()) : undefined
}

export const DEFAULT_ACTION_TYPE_ICON = 'circle-dot'

export interface BuiltInActionConfig {
	isBuiltIn: boolean
	icon: string
	payloadType: string
	description?: string
}

/**
 * Determines if an action type is built-in and returns its configuration
 */
export function getBuiltInActionConfig(actionType: ActionType): BuiltInActionConfig {
	const type = actionType.type as BuiltInActionType

	switch (type) {
		case 'update_firmware':
			return {
				isBuiltIn: true,
				icon: 'package',
				payloadType: 'file',
				description: 'Update device firmware',
			}

		case 'update_config':
			return {
				isBuiltIn: true,
				icon: 'settings',
				payloadType: 'json',
				description: 'Update device configuration',
			}

		case 'update_geofence':
			return {
				isBuiltIn: true,
				icon: 'map-pin',
				payloadType: 'json',
				description: 'Update geofence settings',
			}

		case 'send_file':
			return {
				isBuiltIn: true,
				icon: 'send',
				payloadType: 'file',
				description: 'Send file to device',
			}

		case 'launch_shell':
			return {
				isBuiltIn: true,
				icon: 'terminal',
				payloadType: 'none',
				description: 'Launch remote shell session',
			}

		case 'send_script':
			return {
				isBuiltIn: true,
				icon: 'file',
				payloadType: 'file',
				description: 'Send and execute script',
			}

		case 'retry_action':
			return {
				isBuiltIn: true,
				icon: 'rotate-ccw',
				payloadType: 'none',
				description: 'Retry failed action',
			}

		// Organization-level action types
		case 'reprovision':
			return {
				isBuiltIn: true,
				icon: 'refresh-ccw',
				payloadType: 'none',
				description: 'Reprovision device',
			}

		case 'detach':
			return {
				isBuiltIn: true,
				icon: 'unlink',
				payloadType: 'json',
				description: 'Move device back to root project',
			}

		case 'attach':
			return {
				isBuiltIn: true,
				icon: 'link',
				payloadType: 'none',
				description: 'Attach device to a project',
			}

		default:
			return {
				isBuiltIn: false,
				icon: actionType.icon ?? DEFAULT_ACTION_TYPE_ICON,
				payloadType: actionType.payload_type,
				description: 'Custom action type',
			}
	}
}

export function isBuiltInActionType(actionType: ActionType): boolean {
	return BUILT_IN_ACTION_TYPES.includes(actionType.type as BuiltInActionType)
}

/**
 * Checks if an action type can be deleted (only custom actions can be deleted)
 */
export function canDeleteActionType(actionType: ActionType): boolean {
	return !isBuiltInActionType(actionType)
}

/**
 * Gets the appropriate icon for an action type (built-in or custom)
 */
export function getActionTypeIcon(actionType: ActionType): string {
	const config = getBuiltInActionConfig(actionType)
	return config.icon
}

/**
 * Gets the appropriate payload type for an action type
 * Uses the actual API response payload_type value
 */
export function getActionTypePayloadType(actionType: ActionType): string {
	if (!actionType) {
		return ''
	}
	if (actionType.type === 'update_firmware') {
		return 'Firmware'
	}
	const payloadType = actionType.payload_type?.toLowerCase()
	if (payloadType === 'config') {
		return 'Geofence'
	}
	return actionType.payload_type || ''
}

export const PAYLOAD_TYPE_OPTIONS = [
	{ label: 'None', value: 'none' },
	{ label: 'JSON', value: 'json' },
	{ label: 'File', value: 'file' },
	{ label: 'Geofence', value: 'config' },
] as const

export const TIMEOUT_TRIGGER_OPTIONS = [
	{ label: 'In Progress', value: 'in_progress' },
	{ label: 'Queued', value: 'queued' },
] as const

/**
 * Resolves the action type shown to users. `renew_cert` is an internal type — surface its
 * reprovision intent (`reprovision_type`) instead, and present `transfer` as "reprovision".
 * Falls back to the raw type when no reprovision intent is present.
 */
export function resolveActionTypeLabel(type: string, reprovisionType?: string | null): string {
	if (type !== 'renew_cert') {
		return type
	}
	if (reprovisionType === 'transfer') {
		return 'reprovision'
	}
	return reprovisionType || type
}

/**
 * Action state as users read it. `timed_out_exhausted` means the retries ran out after a timeout,
 * which the rest of the UI presents as a plain failure.
 */
export function formatActionStateLabel(state: string): string {
	const normalized = state.toLowerCase()
	if (normalized === 'timed_out_exhausted' || normalized === 'timeout_exhausted') {
		return 'Failed'
	}
	const words = normalized.replace(/_/g, ' ').trim()
	return words.charAt(0).toUpperCase() + words.slice(1)
}

/** States the UI folds into "Failed": a plain failure and retries exhausted after a timeout. */
export const FAILED_ACTION_STATES = new Set(['failed', 'timed_out_exhausted'])

/** Terminal action states — once an action reaches these it can no longer be completed. */
export const TERMINAL_ACTION_STATES = new Set(['completed', 'failed', 'marked_as_completed', 'timed_out_exhausted'])

/**
 * An action is still ongoing (completable) unless it has reached a terminal state.
 * Normalizes casing — backend may send mixed-case states on the platform path.
 */
export function isActiveActionState(state: string | undefined): boolean {
	const normalized = (state ?? '').toLowerCase()
	return normalized !== '' && !TERMINAL_ACTION_STATES.has(normalized)
}

/**
 * Formats an action type string to a human-readable name
 * e.g., 'update_firmware' -> 'Update Firmware'
 */
export function formatActionTypeName(actionType: string): string {
	if (!actionType) {
		return ''
	}
	const stringVal = typeof actionType === 'string' ? actionType : String(actionType)
	return stringVal
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

/**
 * A deactivated type stays in `GET /v3/action-types` so the settings page can list it under its
 * Inactive tab. Absent `active` means active: the field is only set once a type has been toggled.
 */
export function isActionTypeActive(actionType: ActionType): boolean {
	return actionType.active !== false
}
